import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiArrowRight, FiTrendingDown } = FiIcons;

const Problem = () => {
  const painPoints = [
    "You're exhausted but your mind races the moment you hit the pillow",
    "Chugging coffee just to feel halfway human, only to crash by 3 PM",
    "You're one of nearly two-thirds of Americans taking melatonin supplements, but still feel groggy",
    "Energy drinks have become a food group",
    "Snapping at loved ones because your emotional regulation is shot",
    "Staring at a screen for an hour, unable to start a single task",
    "You've tried everything: sleep apps, white noise, expensive mattresses"
  ];

  const cycleSteps = [
    { label: "Poor sleep", color: "bg-red-100 text-red-800" },
    { label: "Weakened brain control", color: "bg-orange-100 text-orange-800" },
    { label: "Racing thoughts", color: "bg-yellow-100 text-yellow-800" },
    { label: "Poor sleep", color: "bg-red-100 text-red-800" }
  ];

  const stats = [
    {
      text: "Even one night of poor sleep lowers cognitive performance",
      source: "Wikipedia, Sleep Deprivation Effects"
    },
    {
      text: "Self-regulation and self-monitoring take a hit after sleep loss",
      source: "ScienceDirect, Cognitive Function"
    },
    {
      text: "Chronic poor sleep seriously damages working memory, emotional control, and task initiation—the very skills you need to thrive",
      source: "Journal of Clinical Sleep Medicine"
    }
  ];

  return (
    <section id="problem" className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            Why Your Brain Won't Let You Sleep
          </h2>
        </motion.div>

        {/* Sound Familiar Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg mb-16"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Sound familiar?
          </h3>
          
          <div className="grid gap-4">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-200"
              >
                <SafeIcon
                  icon={FiAlertTriangle}
                  className="w-6 h-6 text-accent-magenta flex-shrink-0 mt-1"
                />
                <p className="text-gray-700 font-raleway">{point}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-accent-light/50 rounded-2xl">
            <p className="text-lg text-gray-700 font-raleway leading-relaxed">
              Your executive functions—those mental skills that help you focus, remember, and control impulses—are running low.
            </p>
            <p className="text-lg text-gray-700 font-raleway leading-relaxed mt-4">
              But what if the problem isn't your sleep, but how your brain manages sleep?
            </p>
            <p className="text-lg font-semibold text-accent-magenta mt-4">
              That's where executive function therapy comes in.
            </p>
          </div>
        </motion.div>

        {/* The Vicious Cycle */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg mb-16"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8 text-center">
            The Vicious Cycle
          </h3>
          
          <div className="flex flex-wrap justify-center items-center gap-4">
            {cycleSteps.map((step, index) => (
              <React.Fragment key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className={`px-6 py-4 rounded-full font-semibold ${step.color}`}
                >
                  {step.label}
                </motion.div>
                {index < cycleSteps.length - 1 && (
                  <SafeIcon
                    icon={FiArrowRight}
                    className="w-6 h-6 text-gray-400 hidden sm:block"
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-lg text-gray-700 font-raleway leading-relaxed">
              We don't just help you fall asleep. Executive function therapy targets those brain skills to finally restore your natural sleep-wake cycle—and lasting rest.
            </p>
          </div>
        </motion.div>

        {/* Key Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Key Stats You'll Want to Know
          </h3>
          
          <div className="space-y-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 p-6 bg-gradient-to-r from-accent-light/30 to-accent-purple/20 rounded-xl"
              >
                <SafeIcon
                  icon={FiTrendingDown}
                  className="w-6 h-6 text-accent-magenta flex-shrink-0 mt-1"
                />
                <div>
                  <p className="text-gray-700 font-raleway leading-relaxed">
                    {stat.text}
                    <sup className="text-accent-magenta font-semibold ml-1">
                      [{stat.source}]
                    </sup>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;